import React, { useState, useEffect } from 'react';
// Native MUI Components
import {
  Button,
  Typography,
  Box,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Grid
} from '@mui/material';
import { ViewModule, TableChart } from '@mui/icons-material';
import { Traveller, Trip, Apartment } from '@/types';
import { travellerService } from '@/services/travellerService';
import { apartmentService } from '@/services/apartmentService';
import AddTravellerForm from '@/components/traveller/AddTravellerForm';
import TravellerDataGrid from '@/components/traveller/TravellerDataGrid';
import TravellerCard from './TravellerCard';

interface TravellerListProps {
  trip: Trip;
  onViewModeChange?: (mode: 'grid' | 'table') => void;
  currentViewMode?: 'grid' | 'table';
}

const TravellerList: React.FC<TravellerListProps> = ({ 
  trip, 
  onViewModeChange,
  currentViewMode 
}) => {
  const [viewMode, setViewMode] = useState<'grid' | 'table'>(currentViewMode || 'grid');
  const [travellers, setTravellers] = useState<Traveller[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);

  const handleViewModeChange = (newMode: 'grid' | 'table') => {
    setViewMode(newMode);
    onViewModeChange?.(newMode);
  };

  useEffect(() => {
    if (currentViewMode) {
      setViewMode(currentViewMode);
    }
  }, [currentViewMode]);

  useEffect(() => {
    if (!trip.id) return;
    const unsubscribeTravellers = travellerService.listenToTravellers(trip.id, setTravellers);
    const unsubscribeApartments = apartmentService.listenToApartments(trip.id, setApartments);
    return () => {
      unsubscribeTravellers();
      unsubscribeApartments();
    };
  }, [trip.id]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {viewMode === 'grid' ? (
        <Grid container spacing={2}>
          {travellers.map(traveller => (
            <Grid key={traveller.id} size={{ xs: 12, md: 6, lg: 4 }}>
              <TravellerCard traveller={traveller} trip={trip} apartments={apartments} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <TravellerDataGrid trip={trip} travellers={travellers} apartments={apartments} />
      )}
    </Box>
  );
};

export default TravellerList;
